/**
 * (c) 2023, Center for Computational Thinking and Design at Aarhus University and contributors
 * Modifications (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */

import { AccelerometerData } from "@microbit/microbit-connection";
import React, { useCallback, useMemo, useRef } from "react";
import { RiArrowDropLeftFill } from "react-icons/ri";
import { useAccelerometerListener } from "../hooks/use-accelerometer-listener";
import { Box, Icon, Text } from "@microbit/ui";
import { useGraphColors } from "../hooks/use-graph-colors";
import { getLabelHeights } from "../live-graph-label-config";
import { useSettings } from "../store";
import { smoothenDataPoint } from "./LiveGraph";

interface LiveGraphLabelsProps {
  paused?: boolean;
}

const LiveGraphLabels = ({ paused }: LiveGraphLabelsProps) => {
  const [{ graphColorScheme }] = useSettings();
  const colors = useGraphColors(graphColorScheme);

  const xArrowHeightRef = useRef<HTMLDivElement>(null);
  const xLabelHeightRef = useRef<HTMLParagraphElement>(null);
  const yArrowHeightRef = useRef<HTMLDivElement>(null);
  const yLabelHeightRef = useRef<HTMLParagraphElement>(null);
  const zArrowHeightRef = useRef<HTMLDivElement>(null);
  const zLabelHeightRef = useRef<HTMLParagraphElement>(null);

  const labelConfig = useMemo(
    () => [
      {
        label: "x",
        color: colors.x,
        arrowHeightRef: xArrowHeightRef,
        labelHeightRef: xLabelHeightRef,
      },
      {
        label: "y",
        color: colors.y,
        arrowHeightRef: yArrowHeightRef,
        labelHeightRef: yLabelHeightRef,
      },
      {
        label: "z",
        color: colors.z,
        arrowHeightRef: zArrowHeightRef,
        labelHeightRef: zLabelHeightRef,
      },
    ],
    [colors.x, colors.y, colors.z]
  );

  const dataRef = useRef<{ x: number; y: number; z: number }>({
    x: 0,
    y: 0,
    z: 0,
  });

  const accelerometerListener = useCallback(
    (data: AccelerometerData) => {
      if (paused) {
        return;
      }
      dataRef.current = {
        x: smoothenDataPoint(dataRef.current.x, data.x),
        y: smoothenDataPoint(dataRef.current.y, data.y),
        z: smoothenDataPoint(dataRef.current.z, data.z),
      };
      const labelHeights = getLabelHeights(dataRef.current);
      labelConfig.forEach((config) => {
        const heights = labelHeights.find((v) => v.label === config.label);
        if (
          heights &&
          config.arrowHeightRef.current &&
          config.labelHeightRef.current
        ) {
          config.arrowHeightRef.current.style.transform = `translateY(${heights.arrowHeight}rem)`;
          config.labelHeightRef.current.style.transform = `translateY(${heights.labelHeight}rem)`;
        }
      });
    },
    [paused, labelConfig]
  );

  useAccelerometerListener(accelerometerListener);

  return (
    <Box w={10} h={40} position="relative">
      {labelConfig.map((config) => (
        <React.Fragment key={config.label}>
          <Box
            ref={config.arrowHeightRef}
            ml={-7}
            position="absolute"
            w="fit-content"
            // Graph colours are computed at runtime.
            style={{ color: config.color }}
          >
            <Icon as={RiArrowDropLeftFill} css={{ width: 12, height: 12 }} />
          </Box>
          <Text
            ref={config.labelHeightRef}
            ml={1}
            fontSize="xl"
            position="absolute"
            w="fit-content"
            style={{ color: config.color }}
          >
            {config.label}
          </Text>
        </React.Fragment>
      ))}
    </Box>
  );
};

export default LiveGraphLabels;
